import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Box, Grid, Stack, Typography } from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import { ReactComponent as BackIcon } from "../../../../../vendor/_new/icon/detections/back.svg";
import { PanelSkeleton, DashboardPanelEmptyState } from "../web_application/Dashboard";
import colors from "../../../../../utils/colors";
import useBMConfig from "../../../../../hooks/user/useBMConfig";

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

const BotDetectionList = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const {
    top_source,
    top_path,
    top_ua,
    top_method,
    top_ja3_hash,
    top_region,
    top_host,
    getTopSource,
    getTopPath,
    getTopUA,
    getTopMethod,
    getTopJa3Hash,
    getTopHost,
    getTopRegion,
  } = useBMConfig();

  const [subTitle, key] = useMemo(() => {
    switch (categoryId) {
      case "country":
        return ["Top detections by country", "country_iso_code"];
      case "ua":
        return ["Top user agent", "ua"];
      case "uri":
        return ["Top paths", "uri"];
      case "source_ip":
        return ["Top events by source", "addr"];
      case "method":
        return ["Top methods", "method"];
      case "host":
        return ["Top hosts", "host"];
      case "ja3_hash":
        return ["Top JA3 fingerprints", "ja3_hash"];
      default:
        return ["Top detections by country", "country"];
    }
  }, [categoryId]);

  const detectionList = useMemo(() => {
    switch (categoryId) {
      case "country":
        return top_region;
      case "ua":
        return top_ua;
      case "uri":
        return top_path;
      case "source_ip":
        return top_source;
      case "method":
        return top_method;
      case "host":
        return top_host;
      case "ja3_hash":
        return top_ja3_hash;
      default:
        return [];
    }
  }, [categoryId, top_region, top_ua, top_path, top_source, top_method, top_host, top_ja3_hash]);

  const tmpData = JSON.parse(decodeURIComponent(searchParams.get("data")));
  const siteID = tmpData?.siteID || "all";
  const timeRange = tmpData?.timeRange || "7d";

  useEffect(() => {
    switch (categoryId) {
      case "country":
        getTopRegion(siteID, timeRange, 20, null);
        break;
      case "ua":
        getTopUA(siteID, timeRange, 20, null);
        break;
      case "uri":
        getTopPath(siteID, timeRange, 20, null);
        break;
      case "source_ip":
        getTopSource(siteID, timeRange, 20, null);
        break;
      case "method":
        getTopMethod(siteID, timeRange, 20, null);
        break;
      case "host":
        getTopHost(siteID, timeRange, 20, null);
        break;
      case "ja3_hash":
        getTopJa3Hash(siteID, timeRange, 20, null);
        break;
      default:
        break;
    } // eslint-disable-next-line
  }, [categoryId, getTopSource, getTopPath, getTopUA, getTopMethod, getTopJa3Hash, getTopHost, getTopRegion]);

  const handleDetail = async (categoryId, data) => {
    navigate(`/v2/detection/bot/dashboard/${categoryId}/detail?data=${encodeURIComponent(JSON.stringify({ siteID, timeRange, data }))}`);
  };

  return (
    <>
      <Helmet title="Bot Management" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() => navigate("/v2/detection/bot/dashboard")}
          sx={{ cursor: "pointer" }}
        >
          <BackIcon style={{ color: colors.primary[300] }} />
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            Bot Management Dashboard
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            /
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            {subTitle}
          </Typography>
        </Stack>
        <Box sx={{ width: "100%", border: `solid 1px ${colors.neutral[200]}`, p: 2, borderRadius: "8px" }}>
          <Grid container>
            {detectionList === null && <PanelSkeleton />}
            {detectionList !== null && detectionList.length === 0 && <DashboardPanelEmptyState />}
            {detectionList?.map((item, index) => (
              <Grid key={`waf_event_${index}`} item xs={12}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  spacing={3}
                  onClick={() => handleDetail(categoryId, item?.[key])}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    height: 42,
                    "&:hover": { backgroundColor: colors.primary["alpha"] },
                    cursor: "pointer",
                  }}
                >
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                    {key === "country_iso_code" && (
                      <ReactCountryFlag
                        countryCode={item?.["country_iso_code"]}
                        svg
                        style={{
                          width: "26px",
                          height: "18px",
                        }}
                      />
                    )}
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                      {key === "country_iso_code" ? regionNames.of(item?.["country_iso_code"]) : item?.[key]}
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                    <LayerdIcon icon={DETECTION_ICONS.detection} size={18} color="neutral" />
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600], pr: 3 }}>
                      {item.count}
                    </Typography>
                    <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                  </Stack>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </>
  );
};
export default BotDetectionList;
